import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import { useQueryReportUser } from '../../models/useQueryReportUser'

export interface IUseReportUserPresenterParams {
  /** Route param — the FS/Page owns routing context (ADR-9) */
  userId: number
  /** False when another report config's header hook should fetch instead */
  enabled?: boolean
  /**
   * Toasts a non-404 read failure. Every observer of a react-query v3 query
   * runs its own onError, so only one caller of the shared header query
   * (the detail Feature Set) opts in.
   */
  notifyOnError?: boolean
}

/**
 * Hook-only presenter (ADR-2): resolves the user's full name for the
 * detail breadcrumb "{Orders by user} / {user name}" and the identity block
 * above the tiles: avatar, name and the department's "Location - Department"
 * context, omitted when either part is missing.
 */
export const useReportUserPresenter = ({
  userId,
  enabled = true,
  notifyOnError = false,
}: IUseReportUserPresenterParams) => {
  const intl = useProcurifyIntl()

  // The model reports an unknown id through `isNotFound` and passes only
  // other failures here.
  const handleError = (error: unknown) => {
    toast.error({
      title: intl.formatMessage(messages.errorToastTitle),
      children:
        getMessageFromError(error) ??
        intl.formatMessage(messages.loadErrorFallbackMessage),
    })
  }

  const { user, isLoading, isNotFound } = useQueryReportUser(userId, enabled, {
    onError: notifyOnError ? handleError : undefined,
  })

  return {
    data: {
      userFullName: user?.fullName ?? null,
      profileImage: user?.profileImage || null,
      contextLabel:
        user?.secondaryCategory?.name &&
        user.secondaryCategory.primaryCategory?.name
          ? user.secondaryCategory.nameWithPrimaryCategory
          : null,
    },
    states: {
      isLoading,
      isNotFound,
    },
  }
}
